class FeatureBlocksSlider extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('.slider');
    this.sliderItems = this.querySelectorAll('.slider__slide');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.dots = this.querySelectorAll('.slider-dot');
    this.currentSlide = 0;
    this.totalSlides = this.sliderItems.length;
    
    if (!this.slider || !this.sliderItems.length) return;
    
    // Initialize event listeners
    this.initButtons();
    this.initDots();
    this.initScrollListener();
    
    // Check if the slider is visible on mobile only
    this.checkSliderVisibility();
    window.addEventListener('resize', this.checkSliderVisibility.bind(this));
  }
  
  /**
   * Initialize scroll event listener to handle manual swiping
   */
  initScrollListener() {
    if (!this.slider) return;
    
    // Set up snap scrolling for full-width slides
    const isMobile = window.innerWidth < 750;
    if (isMobile) {
      this.slider.style.scrollSnapType = 'x mandatory';
    }
    
    // Use throttled scroll event to detect slide changes during manual swipes
    let scrollTimeout;
    this.slider.addEventListener('scroll', () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      
      scrollTimeout = setTimeout(() => {
        // Calculate which slide is currently most visible
        const sliderWidth = this.slider.clientWidth;
        const currentIndex = Math.round(this.slider.scrollLeft / sliderWidth);
        
        // If the slide has changed, update the dots
        if (currentIndex !== this.currentSlide && currentIndex < this.totalSlides) {
          this.currentSlide = currentIndex;
          this.updateDots(currentIndex);
        }
      }, 50); // Small timeout to throttle the scroll event
    });
  }
  
  /**
   * Update the dot indicators to show the current slide
   * @param {number} index - The index of the current slide
   */
  updateDots(index) {
    if (!this.dots.length) return;
    
    // Remove active class from all dots
    this.dots.forEach(dot => {
      dot.classList.remove('slider-dot--active');
    });
    
    // Add active class to current dot
    if (this.dots[index]) {
      this.dots[index].classList.add('slider-dot--active');
    }
  }
  
  /**
   * Initialize dot indicator event listeners
   */
  initDots() {
    if (!this.dots.length) return;
    
    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });
  }
  
  /**
   * Initialize button event listeners
   */
  initButtons() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', this.onPrevButtonClick.bind(this));
    }
    
    if (this.nextButton) {
      this.nextButton.addEventListener('click', this.onNextButtonClick.bind(this));
    }
  }
  
  /**
   * Handle previous button click
   */
  onPrevButtonClick() {
    this.goToPrevSlide();
  }
  
  /**
   * Handle next button click
   */
  onNextButtonClick() {
    this.goToNextSlide();
  }
  
  /**
   * Go to the previous slide
   */
  goToPrevSlide() {
    if (!this.slider) return;
    
    // Find the current slide by comparing offset with scroll position
    const slideWidth = this.sliderItems[0].offsetWidth;
    const currentIndex = Math.round(this.slider.scrollLeft / slideWidth);
    
    // Go to previous slide or loop to the end
    const prevIndex = Math.max(0, currentIndex - 1);
    this.goToSlide(prevIndex);
  }
  
  /**
   * Go to the next slide
   */
  goToNextSlide() {
    if (!this.slider) return;
    
    // Find the current slide by comparing offset with scroll position
    const slideWidth = this.sliderItems[0].offsetWidth;
    const currentIndex = Math.round(this.slider.scrollLeft / slideWidth);
    
    // Go to next slide or loop to the beginning
    const nextIndex = Math.min(this.totalSlides - 1, currentIndex + 1);
    this.goToSlide(nextIndex);
  }
  
  /**
   * Go to a specific slide by index
   * @param {number} index - The index of the slide to go to
   */
  goToSlide(index) {
    if (!this.slider || !this.sliderItems[index]) return;
    
    // For full-width slides on mobile, use the slider width as the scroll unit
    const isMobile = window.innerWidth < 750;
    let targetScrollPosition;
    
    if (isMobile) {
      // On mobile, each slide is full-width, so multiply by the index
      targetScrollPosition = index * this.slider.clientWidth;
    } else {
      // On desktop, use the original offset calculation
      targetScrollPosition = this.sliderItems[index].offsetLeft - this.sliderItems[0].offsetLeft;
    }
    
    // Use scrollTo with smooth behavior for the animation
    this.slider.scrollTo({
      left: targetScrollPosition,
      behavior: 'smooth'
    });
    
    // Update the current slide index
    this.currentSlide = index;
    
    // Update the dot indicators
    this.updateDots(index);
  }
  
  /**
   * Check if the slider should be visible based on screen size
   */
  checkSliderVisibility() {
    const isMobile = window.innerWidth < 750;
    
    // Only show slider controls on mobile, but don't disable the buttons
    // (they should still be clickable when visible)
    if (this.prevButton && this.nextButton) {
      const sliderButtons = this.querySelector('.slider-buttons');
      if (sliderButtons) {
        sliderButtons.classList.toggle('hide', !isMobile);
      }
    }
  }
}

// Register the custom element
customElements.define('feature-blocks-slider', FeatureBlocksSlider);

// Initialize the slider on existing elements
document.addEventListener('DOMContentLoaded', () => {
  // Check if the slider component is being used
  const sliderComponent = document.querySelector('slider-component');
  if (sliderComponent) {
    // Add intersection observer to handle slider updates on scroll
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      
      // Update slider for accurate calculations after it becomes visible
      const sliderItems = sliderComponent.querySelectorAll('.slider__slide');
      if (sliderItems.length) {
        const counterTotal = sliderComponent.querySelector('.slider-counter--total');
        if (counterTotal) {
          counterTotal.textContent = sliderItems.length;
        }
      }
      
      observer.unobserve(sliderComponent);
    };
    
    const observer = new IntersectionObserver(handleIntersection);
    observer.observe(sliderComponent);
  }
});